// Page-open bridge — lets a workspace-module widget body (or any module
// code) open one of the MODULE'S OWN registered pages in the shell PageSlot,
// exactly like a navbar tab does. Scope-guarded: routes no installed module
// serves (`_servesPage` below: root.workspaceProject.pages, or an installed
// app's own root.workspaceApps[].pages) are refused, so a module can never
// steer the shell to arbitrary surfaces. Sibling of the moduleData /
// moduleAiChat / moduleFileUpload bridges (workspaceProjects.js injection
// site).
//
// Invoked as el.call('moduleOpenPage', '/route') — the function registry
// binds `this` to the calling element, which openAppPage needs for its
// state / router access.
import { openAppPage } from './searchApps.js'
import { slotHostOf } from './pageParam.js'
import { moduleViewAsKey } from './workspaceProjects.js'
import { moduleRecordRoutes } from './moduleRecordRoutes.js'
import { shellStripWorkspacePrefix } from './shellSharedDelegates.js'


// RAIL-TRANSITION (tickets/fable.md) — the `?viewAs=` durability fix's
// original write site (app.js `_syncShellRoute`, via the invisible
// `ShellRouteSync` component) never actually ran on a View-as click:
// `ShellRouteSync`'s `subscribeTo` only names
// `['currentPath', '_chatDrawerOpen', 'assistantOpen', 'assistantDock',
// 'assistantWidth']`, and neither a persona's `s.root.update({bfPersonaOverride,
// bfView})` nor `moduleOpenPage`'s own `_canvasSlot` write touches any of
// those — so the reactive component never re-fires and `?viewAs=` never
// lands (Chrome-MCP-confirmed live, 2026-08-14: `?page=` updates every
// switch, `?viewAs=` never once across ten). `subscribeTo`'s key list is
// also resolved ONCE at element-create time (registerSubscribeToEffects,
// smbls create.js) — a dynamic function form keyed off
// `moduleViewAsKey(root)` would still be frozen at shell-mount time, before
// any module (and its `chrome.viewAsKey`) has loaded, so widening the
// reactive subscription can't work either.
//
// Fixed here instead: `moduleOpenPage` is the ONE bridge every View-as
// persona-switch and every other module-page-open call funnels through
// (BfNav.js pairs its override write with this call unconditionally), so
// syncing `?viewAs=` imperatively, right here, right after the slot opens,
// is deterministic — no reactive gating, no create-time freeze, no missed
// tick. Generic: keyed off the active module's `chrome.viewAsKey` (never a
// module-specific name), so any module gets this for free. Read/restore
// half is `_restoreViewAsParam` (workspaceProjects.js), unchanged.
const _syncViewAsUrl = (root) => {
  if (typeof window === 'undefined') return
  const key = moduleViewAsKey(root)
  if (!key) return
  try {
    const url = new URL(window.location.href)
    const cur = url.searchParams.get('viewAs')
    const val = root ? root[key] : null
    const next = typeof val === 'string' && val && val !== 'admin' ? val : null
    if (next === cur) return
    if (next) url.searchParams.set('viewAs', next)
    else url.searchParams.delete('viewAs')
    const nextUrl = url.pathname + url.search + url.hash
    const current = window.location.pathname + window.location.search + window.location.hash
    if (nextUrl !== current) window.history.replaceState(window.history.state, '', nextUrl)
  } catch (err) {
    console.warn('[moduleOpenPage] viewAs url sync failed:', err?.message || err)
  }
}

export const buildModuleOpenBridge = (root) => {
  return function moduleOpenPage(route) {
    try {
      const key = String(route || '')
      // A1-06 — the scope guard resolves the PATH, the open carries the FULL
      // route. A module opening one of its own records passes the params that
      // identify it (`/vault-rooms?tenant=longbow&item=<id>`); an exact
      // `pages[key]` lookup never matches those, so every deep open was
      // REFUSED here (returned false, nothing opened) and the module had to
      // latch the record in root state instead — nothing shareable, nothing
      // reload-safe. Same normalization openAppPage and PageSlot's `_defFor`
      // already apply; the guard is unchanged in strength, only in what it
      // compares (a route outside the module's own pages is still refused).
      const path = key.split('?')[0].split('#')[0]
      if (!key || !(_servesPage(root, key) || _servesPage(root, path))) return false
      openAppPage.call(this, key, { preferSlot: true })
      _syncViewAsUrl(root)
      return true
    } catch (e) {
      console.warn('[moduleOpenPage] failed:', e?.message || e)
      return false
    }
  }
}

// ── setModuleRouteQuery — the URL seam for a SAME-PAGE record open ─────────
//
// A module that is already ON one of its pages must NOT navigate to open a
// record: `openAppPage(..., { preferSlot: true })` mounted a SECOND instance
// of the page it was already showing (the doubled-sheet bug; the module side
// now simply does not navigate). That left it with no way to put `?item=<id>`
// in the address at all, so a same-page open was unshareable and lost on
// reload.
//
// The SHELL owns the URL, so the shell provides the write:
//
//   el.call('setModuleRouteQuery', { item: 'lb-1' })   // open  -> ?item=lb-1
//   el.call('setModuleRouteQuery', { item: null })     // one param away
//   el.call('setModuleRouteQuery', null)               // sheet closed -> clear
//
// Contract:
//   • history.replaceState ONLY for the top-level query — no navigation
//     event, no new page instance, no router dispatch. (The AppShell history
//     patch fires `locationchange` on every replaceState, and the shell's
//     `_closeOnNav` acts on PATHNAME changes only, so a query-only write
//     disturbs nothing. The no-op guard below keeps an unchanged URL from
//     firing it at all — the same guard PageSlot._writeSlotUrl carries, for
//     the same reason.)
//   • A key the PANE route already carries is patched there as well
//     (`_patchPaneRoute` below), with no re-mount. That address write is
//     PageSlot's own, and it records it like any pane route change.
//   • Scope-guarded like moduleOpenPage: it writes only while the CALLER'S
//     surface IS one of this module's own pages (the route of the pane the
//     caller sits in; outside a pane, the open primary PageSlot route or the
//     pathname). A module can never rewrite a shell route's query.
//   • The address bar is the PRIMARY pane's mirror (PageSlot `_slotUrlNext`).
//     A caller in the SECOND pane writes its own pane route only, never the
//     address (`_moduleSurface` below).
//   • Shell-owned params are untouchable: `page` is the PageSlot key and
//     `viewAs` is the persona pin, both written by the shell itself.
//   • `null` clears the params this module DECLARES for the current route
//     (manifest `collections` -> root.workspaceProject.recordRoutes), so the
//     close path needs no bookkeeping and can never strip a param it does not
//     own.
//   • The value survives a reload with no extra work: it is a real query
//     param, so the page reads it from `location.search` on a cold mount
//     exactly as it does after a slot open (A1-06).
//
// Returns true when the URL changed.
const _RESERVED_SHELL_PARAMS = new Set(['page', 'viewAs'])

// A page route one of the installed modules serves: the takeover module's
// (and the composed stash's) `root.workspaceProject.pages`, or an ADDITIVE
// app's own list, `root.workspaceApps[].pages` — an app installed from the
// App Store serves its pages there, and the composed stash may not carry them
// yet (current-2a 2026-09-28: the seam answered only for the takeover, so an
// installed app — /realestate?unit=… — could not write its own record param).
const _servesPage = (root, path) => {
  if (!path) return false
  const pages = (root && root.workspaceProject && root.workspaceProject.pages) || {}
  if (pages[path]) return true
  const apps = root && Array.isArray(root.workspaceApps) ? root.workspaceApps : []
  return apps.some((a) => a && Array.isArray(a.pages) && a.pages.includes(path))
}

// THE CALLER'S SURFACE — `{ route, second }`, or null when the caller is on
// no page an installed module serves. `host` is the PageSlot keep-alive host
// the caller sits in (`slotHostOf(this)`), or null outside a pane.
//
//   • In the SECOND pane: that pane's own route (`host._slotPath`, the
//     `root._canvasSlotSecond` value PageSlot stamped on it). `second: true`
//     tells the bridge to stop at the pane route: the address bar mirrors the
//     PRIMARY pane only (PageSlot `_slotUrlNext`; the split pane is transient,
//     never in the address), and `readPageParam` never reads the address for
//     a second-pane caller (pageParam.js `pageParams`).
//   • Anywhere else (the primary pane, or no pane at all): the address bar's
//     own surface, unchanged — the primary pane's route, else the pathname.
//
// The guard used to read `root._canvasSlot` for every caller (lane RQ-2,
// 2026-10-30). So a page in the SECOND pane was refused in full, pane route
// and address, whenever the primary showed a shell route (`/chat`, `/tasks`):
// an App Store app's consumed create link (`/catalog?new=1&name=…`) stayed in
// `root._canvasSlotSecond`, and a close of the primary pane PROMOTES that route
// into the primary and into `?page=` (PageSlot `_closeSlot`), where the
// promoted page and a reload opened a second form. Workspace pages had the
// same gap. And when the primary DID show a module page, a second-pane
// `null` cleared the params the PRIMARY's route declares, and a second-pane
// write reached the address, the primary's mirror.
const _SECOND_PANE = '_canvasSlotSecond'

const _moduleSurface = (root, host) => {
  if (typeof window === 'undefined') return null
  if (host && host._slotPaneKey === _SECOND_PANE) {
    const p = String(host._slotPath).split('?')[0].split('#')[0]
    return _servesPage(root, p) ? { route: p, second: true } : null
  }
  const slot = root && root._canvasSlot
  const p = slot
    ? String(slot).split('?')[0].split('#')[0]
    : shellStripWorkspacePrefix(window.location.pathname)
  return _servesPage(root, p) ? { route: p, second: false } : null
}

// The params the installed modules DECLARE for a route — every record route
// live in this workspace, the takeover's and every installed app's
// (moduleRecordRoutes: the ONE composed reader the record links use).
const _declaredParamsFor = (root, route) => {
  const out = new Set()
  for (const r of Object.values(moduleRecordRoutes(root))) {
    if (r && r.param && r.route === route) out.add(String(r.param))
  }
  return out
}

// ── The PANE half ────────────────────────────────────────────────────────
//
// A record param can ride in TWO places: the top-level query (a same-page
// open writes it there) and the pane's OWN route — `root._canvasSlot`, the
// `?page=` value — when the page was OPENED on that record (a deep link, an
// Open from another page, a cold load of a shared URL). The top-level write
// alone left the pane route naming the record, and the pane route WINS in
// `readPageParam`, so:
//   • Close cleared the top-level `item` and the address still named the
//     record inside `?page=` — a reload, a shared link or Back re-opened what
//     the reader had just closed. Measured on vault.pr (shell 7a7f367b1,
//     VaultOS 1.44.0), 2026-09-21: close → `?page=/vault-rooms?tenant=…&
//     item=6aa16…&tenant=…`, reload → the sheet is open again.
//   • A second open on the same page wrote `item=NEW` at the top level while
//     the pane route kept `item=OLD`, and a reload opened the OLD record.
//
// So a key the pane route ALREADY carries is patched there too — never
// added there (the top level carries a same-page open, exactly as before).
// The bridge still never writes `?page=` itself: it moves the pane's state
// and PageSlot's own URL sync writes the address (`_slotUrlNext` retires
// the keys it mirrored for the old route and mirrors the new one).
//
// NO RE-MOUNT: PageSlot reads "same route, different query" as a deep-link
// change and re-mounts the page (`_mountPane`: `live._slotPath === path` is
// the only keep-alive hit). The host is stamped with the new route BEFORE
// the state write, so the mount pass sees the page it already has — scroll,
// inputs and in-flight loads survive a close.
//
// SCOPE: the pane must show a page an installed module serves — the SAME
// `_servesPage` the surface guard reads (the composed stash's pages OR an
// installed app's own `root.workspaceApps[].pages`). This check used to read
// `root.workspaceProject.pages` alone, and the composed stash does not
// always carry the apps' pages (an app-only workspace's "no module" pass
// publishes `workspaceProject: null`; a takeover republish writes the
// takeover's pages only), while `root.workspaceApps[]` does. So an App Store
// app's clear reached the address bar and never its pane route: Catalog's
// `?new=1&name=` (Inventory's "Create in Products") and Orders' `?new=1&
// party=` stayed in `?page=`, and a re-mount or a reload of the pane opened
// a second create form (lane RQ, 2026-10-30).
//
// Returns true when the pane route changed.
const _patchPaneRoute = (el, root, entries) => {
  const host = slotHostOf(el)
  if (!host || !root || typeof root.update !== 'function') return false
  const paneKey = host._slotPaneKey || '_canvasSlot'
  const current = root[paneKey]
  if (typeof current !== 'string' || !current || current !== host._slotPath) return false
  const hashAt = current.indexOf('#')
  const hash = hashAt === -1 ? '' : current.slice(hashAt)
  const bare = hashAt === -1 ? current : current.slice(0, hashAt)
  const qAt = bare.indexOf('?')
  if (qAt === -1) return false
  const path = bare.slice(0, qAt)
  if (!_servesPage(root, path)) return false
  const query = new URLSearchParams(bare.slice(qAt + 1))
  let changed = false
  for (const [rawKey, rawVal] of entries) {
    const key = String(rawKey || '').trim()
    if (!key || _RESERVED_SHELL_PARAMS.has(key) || !query.has(key)) continue
    const val = rawVal == null ? '' : String(rawVal)
    if (!val) query.delete(key)
    else if (query.get(key) !== val) query.set(key, val)
    else continue
    changed = true
  }
  if (!changed) return false
  const qs = query.toString()
  const next = path + (qs ? '?' + qs : '') + hash
  host._slotPath = next
  root.update({ [paneKey]: next })
  return true
}

export const buildModuleRouteQueryBridge = (root) => {
  return function setModuleRouteQuery(patch) {
    try {
      if (typeof window === 'undefined') return false
      const surface = _moduleSurface(root, slotHostOf(this))
      if (!surface) return false
      const entries =
        patch == null
          ? [..._declaredParamsFor(root, surface.route)].map((k) => [k, null])
          : Object.entries(patch || {})
      // The pane first: its URL sync rewrites the address, so the top-level
      // write below must read the address AFTER it, or it would put back the
      // keys the sync just retired.
      const paneChanged = _patchPaneRoute(this, root, entries)
      // A second-pane caller ends at its own pane route: the address is the
      // primary's mirror. A key its route does not carry is written nowhere
      // (the pane half never adds keys; the app's state holds the open).
      if (surface.second) return paneChanged
      const url = new URL(window.location.href)
      for (const [rawKey, rawVal] of entries) {
        const key = String(rawKey || '').trim()
        if (!key || _RESERVED_SHELL_PARAMS.has(key)) continue
        const val = rawVal == null ? '' : String(rawVal)
        if (!val) url.searchParams.delete(key)
        else url.searchParams.set(key, val)
      }
      const next = url.pathname + url.search + url.hash
      const current = window.location.pathname + window.location.search + window.location.hash
      if (next === current) return paneChanged
      window.history.replaceState(window.history.state, '', next)
      return true
    } catch (e) {
      console.warn('[setModuleRouteQuery] failed:', e?.message || e)
      return false
    }
  }
}
